import defaultContent from "../../../../content/downlineProfile";
import "./OrderHistory.scss";
import { getLongPeriodFormat } from "../../../../common/util/period";
import { storedLocale } from "../../../Authentication/util";
import useWindowSize from "../../../../common/hooks/useWindowSize";
import LeafTag from "../../../../common/leaf/LeafTag/LeafTag";
import { useTranslation } from "react-i18next";
import { IconOrder } from "../../../../common/icons/IconOrder/IconOrder";

type Transaction = {
  OrderDate: string;
  InvoiceNumber: string;
  OrderType: string;
  PV: {
    Formatted: string;
    Numeric: number;
  };
  BV: {
    Formatted: string;
    Numeric: number;
  };
};

type MonthlyTransactions = {
  Period: number;
  Transactions: Transaction[];
};

type CustomerOrderHistory = {
  TransactionHistory: MonthlyTransactions[];
};

const MOCK_CUSTOMER_ORDERS: CustomerOrderHistory = {
  TransactionHistory: [
    {
      Period: 202610,
      Transactions: [
        {
          OrderDate: new Date("10/21/2026").toISOString(),
          InvoiceNumber: "12312413505",
          OrderType: "adjustment",
          PV: {
            Formatted: "600",
            Numeric: 600,
          },
          BV: {
            Formatted: "1200",
            Numeric: 1200,
          },
        },
        {
          OrderDate: new Date("10/21/2026").toISOString(),
          InvoiceNumber: "12312413505",
          OrderType: "rework",
          PV: {
            Formatted: "600",
            Numeric: 600,
          },
          BV: {
            Formatted: "1200",
            Numeric: 1200,
          },
        },
      ],
    },
    {
      Period: 202609,
      Transactions: [
        {
          OrderDate: new Date("9/26/2026").toISOString(),
          InvoiceNumber: "829420124421",
          OrderType: "order",
          PV: {
            Formatted: "600",
            Numeric: 600,
          },
          BV: {
            Formatted: "1200",
            Numeric: 1200,
          },
        },
        {
          OrderDate: new Date("9/3/2026").toISOString(),
          InvoiceNumber: "2423992082089",
          OrderType: "return",
          PV: {
            Formatted: "-240",
            Numeric: -240,
          },
          BV: {
            Formatted: "-480",
            Numeric: -480,
          },
        },
      ],
    },
  ],
};

const getShortMonthDayYear = (period: string, locale: string) => {
  const shortDateFormatter = Intl.DateTimeFormat(locale, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    calendar: "gregory",
  });

  const shortDate = shortDateFormatter.format(Date.parse(period));
  return shortDate;
};

type OrderTypeLabels = {
  order: string;
  return: string;
  rework: string;
  adjustment: string;
};

const getOrderTypeTag = (orderType: string, labels: OrderTypeLabels) => {
  switch (orderType) {
    case "order":
      return <LeafTag variant="medGreen">{labels.order}</LeafTag>;
    case "return":
      return <LeafTag variant="lightRed">{labels.return}</LeafTag>;
    case "rework":
      return <LeafTag variant="lightGray">{labels.rework}</LeafTag>;
    case "adjustment":
      return <LeafTag variant="lightYellow">{labels.adjustment}</LeafTag>;
  }
};

const getOrderTypeLabels = (t: (key: string, defaultValue: string) => string): OrderTypeLabels => ({
  order: t("orderHistory.order", defaultContent.orderHistory.order),
  return: t("orderHistory.return", defaultContent.orderHistory.return),
  rework: t("orderHistory.rework", defaultContent.orderHistory.rework),
  adjustment: t("orderHistory.adjustment", defaultContent.orderHistory.adjustment),
});

const DesktopOrderHistoryTable = ({ data }: { data: CustomerOrderHistory }) => {
  const { t } = useTranslation(["downlineProfile"]);
  const orderTypeLabels = getOrderTypeLabels(t);

  return (
    <div className="CustProfOrderHistoryTable">
      <div className="CustProfOrderHistoryTable__header">
        <span>{t("orderHistory.orderDate", defaultContent.orderHistory.orderDate)}</span>
        <span>{t("orderHistory.invoiceNumber", defaultContent.orderHistory.invoiceNumber)}</span>
        <span>{t("orderHistory.orderType", defaultContent.orderHistory.orderType)}</span>
        <span>{t("orderHistory.pv", defaultContent.orderHistory.pv)}</span>
        <span>{t("orderHistory.bv", defaultContent.orderHistory.bv)}</span>
      </div>
      <div className="CustProfOrderHistoryTable__body">
        {data.TransactionHistory.map((history) => (
          <div className="CustProfOrderHistoryTable__rowGroup">
            <div className="CustProfOrderHistoryTable__monthHeader">
              {getLongPeriodFormat(history.Period.toString(), storedLocale())}
            </div>
            <div className="CustProfOrderHistoryTable__rows">
              {history.Transactions.map((trx) => (
                <div className="CustProfOrderHistoryTable__row">
                  <span>{getShortMonthDayYear(trx.OrderDate, storedLocale())}</span>
                  <span>#{trx.InvoiceNumber}</span>
                  <span>{getOrderTypeTag(trx.OrderType, orderTypeLabels)}</span>
                  <span>{trx.PV.Formatted}</span>
                  <span>{trx.BV.Formatted}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MobileOrderHistoryCards = ({ data }: { data: CustomerOrderHistory }) => {
  const { t } = useTranslation(["downlineProfile"]);
  const orderTypeLabels = getOrderTypeLabels(t);

  return (
    <div className="CustProfOrderHistoryCards">
      {data.TransactionHistory.map(({ Period, Transactions }) =>
        Transactions.map((trx) => (
          <div className="CustProfOrderHistoryCards__card">
            <div className="CustProfOrderHistoryCards__cardHeader">
              {getLongPeriodFormat(Period.toString(), storedLocale())}
            </div>
            <div className="CustProfOrderHistoryCards__cardRow">
              <span className="CustProfOrderHistoryCards__cardRowTitle">
                {t("orderHistory.orderDate", defaultContent.orderHistory.orderDate)}
              </span>
              <span>{getShortMonthDayYear(trx.OrderDate, storedLocale())}</span>
            </div>
            <div className="CustProfOrderHistoryCards__cardRow">
              <span className="CustProfOrderHistoryCards__cardRowTitle">
                {t("orderHistory.invoiceNumber", defaultContent.orderHistory.invoiceNumber)}
              </span>
              <span>#{trx.InvoiceNumber}</span>
            </div>
            <div className="CustProfOrderHistoryCards__cardRow">
              <span className="CustProfOrderHistoryCards__cardRowTitle">
                {t("orderHistory.orderType", defaultContent.orderHistory.orderType)}
              </span>
              <span>{getOrderTypeTag(trx.OrderType, orderTypeLabels)}</span>
            </div>
            <div className="CustProfOrderHistoryCards__cardRow">
              <span className="CustProfOrderHistoryCards__cardRowTitle">
                {t("orderHistory.pv", defaultContent.orderHistory.pv)}
              </span>
              <span>{trx.PV.Formatted}</span>
            </div>
            <div className="CustProfOrderHistoryCards__cardRow">
              <span className="CustProfOrderHistoryCards__cardRowTitle">
                {t("orderHistory.bv", defaultContent.orderHistory.bv)}
              </span>
              <span>{trx.BV.Formatted}</span>
            </div>
          </div>
        )),
      )}
    </div>
  );
};

const NoDataCard = () => {
  const { t } = useTranslation(["downlineProfile"]);

  return (
    <div className="CustProfOrderHistory__noData">
      <IconOrder />
      <p>{t("", defaultContent.orderHistory.noOrdersMessage)}</p>
    </div>
  );
};

export const OrderHistory = () => {
  const { width } = useWindowSize();
  const data = MOCK_CUSTOMER_ORDERS;
  const hasOrders = data && data.TransactionHistory.length > 0;

  if (!hasOrders) {
    return <NoDataCard />;
  }

  return (
    <div className="CustProfOrderHistory">
      {width && width > 1024 ? <DesktopOrderHistoryTable data={data} /> : <MobileOrderHistoryCards data={data} />}
    </div>
  );
};
